'use client';

import { useState, useEffect } from 'react';

import type { SaleButtonStatus } from '../components/sale-buy-button';

interface UseSaleCountdownResult {
  status: SaleButtonStatus;
  timeText: string;
}

function formatTimeRemaining(milliseconds: number): string {
  if (milliseconds <= 0) {
    return '0 seconds';
  }

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    if (remainingHours > 0) {
      return `${days} day${days !== 1 ? 's' : ''} and ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
    }
    return `${days} day${days !== 1 ? 's' : ''}`;
  }

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    if (remainingMinutes > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} and ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
    }
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }

  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    if (remainingSeconds > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} and ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
    }
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}

interface UseSaleCountdownProps {
  startTime: string;
  endTime: string;
  isSoldOut?: boolean;
}

export function useSaleCountdown({
  startTime,
  endTime,
  isSoldOut = false,
}: UseSaleCountdownProps): UseSaleCountdownResult {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();

  if (isSoldOut) {
    return {
      status: 'sold-out',
      timeText: '',
    };
  }

  if (now >= end) {
    return {
      status: 'ended',
      timeText: '',
    };
  }

  if (now < start) {
    return {
      status: 'soon',
      timeText: formatTimeRemaining(start - now),
    };
  }

  return {
    status: 'active',
    timeText: formatTimeRemaining(end - now),
  };
}
